import React from 'react';
import { Star, Reply, Forward } from 'lucide-react';
import {
  Avatar,
  displayNameFromAddr,
  extractEmail,
  getCounterpartyAddr,
  isOutgoingFromAccount,
  isUrgent,
  parseInboxToRecipientEmails,
  type InboxEmail,
  type ChatMessage,
} from '../inboxShared';
import {
  buildInboxAttachmentDownloadUrl,
  parseInboxAttachmentDownloadUrl,
  type InboxAttachmentPreviewTarget,
} from '../inboxAttachmentUtils';
import { InboxAttachmentChip } from './InboxAttachmentChip';
import {
  mailHtmlPreviewClassName,
  prepareMailHtmlForPreview,
  splitMailPlainTextParagraphs,
} from '../../../utils/mailHtmlPreview';

/** Gleiche Kante wie der Betreff (`pl-14` = 40px Avatar + 16px Abstand). */
const THREAD_AVATAR = 40;

function CollapsedQuoteToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const label = expanded ? 'Verlauf ausblenden' : 'Verlauf anzeigen';
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-1.5 inline text-left text-xs font-medium text-app-text-subtle hover:text-app-text-muted hover:underline"
      aria-expanded={expanded}
      aria-label={label}
    >
      {label}
    </button>
  );
}

function MessageQuotedHistory({
  msg,
  sanitizeMailHtml,
  renderTextWithLinks,
  onOpenAttachment,
}: {
  msg: ChatMessage;
  sanitizeMailHtml: (html: string) => string;
  renderTextWithLinks: (text: string) => React.ReactNode;
  onOpenAttachment: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const hasQuote = Boolean(msg.quotedHtml?.trim() || msg.quotedText?.trim());
  if (!hasQuote) return null;

  return (
    <div className="mt-1">
      <CollapsedQuoteToggle expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
      {expanded ? (
        <div className="mt-2 border-t border-app-border/80 pt-2 text-app-text-muted">
          {msg.quotedHtml?.trim() ? (
            <div
              className={`${mailHtmlPreviewClassName(msg.quotedHtml)} !rounded-none !border-0 !bg-transparent !p-0`}
              onClick={onOpenAttachment}
              dangerouslySetInnerHTML={{
                __html: prepareMailHtmlForPreview(sanitizeMailHtml(msg.quotedHtml), {
                  scopeId: `msg-quote-${msg.id}`,
                }),
              }}
            />
          ) : (
            <div className="space-y-3">
              {splitMailPlainTextParagraphs(msg.quotedText || '').map((paragraph, idx) => (
                <p key={idx} className="whitespace-pre-wrap break-words">
                  {renderTextWithLinks(paragraph)}
                </p>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

interface InboxConversationProps {
  selectedMail: InboxEmail;
  messages: ChatMessage[];
  messagesLoading?: boolean;
  accountEmail: string;
  onArchive: () => void;
  onDelete: () => void;
  archiving: boolean;
  deleting: boolean;
  threadScrollRef: React.RefObject<HTMLDivElement | null>;
  sanitizeMailHtml: (html: string) => string;
  renderTextWithLinks: (text: string) => React.ReactNode;
  onOpenAttachment: (attachment: InboxAttachmentPreviewTarget) => void;
  compose: React.ReactNode;
  // mobile
  onBack: () => void;
  onToggleStar?: () => void;
  onForward?: () => void;
  onAiReply?: () => void;
}

export function InboxConversation({
  selectedMail,
  messages,
  messagesLoading = false,
  accountEmail,
  threadScrollRef,
  sanitizeMailHtml,
  renderTextWithLinks,
  onOpenAttachment,
  compose,
  onToggleStar,
  onForward,
}: InboxConversationProps) {
  const [replyOpen, setReplyOpen] = React.useState(false);
  const [openedIds, setOpenedIds] = React.useState<string[]>([]);
  const [showEarlier, setShowEarlier] = React.useState(false);
  React.useEffect(() => {
    setReplyOpen(false);
    setOpenedIds([]);
    setShowEarlier(false);
  }, [selectedMail.id]);

  React.useEffect(() => {
    const el = threadScrollRef.current;
    if (!el) return;
    let inner = 0;
    const scrollToNewestStart = () => {
      const latest = el.querySelector('[data-inbox-latest-message]');
      if (!(latest instanceof HTMLElement) || messages.length <= 1) {
        el.scrollTop = 0;
        return;
      }
      const top = latest.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
      el.scrollTop = Math.max(0, top);
    };
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(scrollToNewestStart);
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [selectedMail.id, messages.length, messagesLoading, threadScrollRef]);
  React.useEffect(() => {
    if (!replyOpen) return;
    const node = threadScrollRef.current?.querySelector('[contenteditable="true"]');
    if (node instanceof HTMLElement) node.focus();
  }, [replyOpen, threadScrollRef]);
  const counterpartyAddr = getCounterpartyAddr(selectedMail, accountEmail);
  const contactName = displayNameFromAddr(counterpartyAddr);
  const accountName = displayNameFromAddr(accountEmail) || 'Ich';
  const isOutgoing = isOutgoingFromAccount(selectedMail.from_addr, accountEmail);
  const accountEmailLower = accountEmail.toLowerCase();
  const selectedMailTo = isOutgoing
    ? parseInboxToRecipientEmails(selectedMail.to_addr)
    : (() => {
        const counterpartyEmail = extractEmail(selectedMail.from_addr || counterpartyAddr).toLowerCase();
        const toBase = parseInboxToRecipientEmails(selectedMail.to_addr).filter(
          (email) => email !== counterpartyEmail,
        );
        return toBase.includes(accountEmailLower) ? toBase : [...toBase, accountEmailLower];
      })();

  const openAttachmentFromMailContent = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[href]');
    const image = target.closest('img[src]');
    const rawUrl = link?.getAttribute('href') ?? image?.getAttribute('src');
    if (!rawUrl) return;
    const attachment = parseInboxAttachmentDownloadUrl(rawUrl);
    if (!attachment) return;
    event.preventDefault();
    event.stopPropagation();
    onOpenAttachment(attachment);
  };

  const renderMessage = (msg: ChatMessage, index: number, collapsed: boolean) => {
    const isOut = msg.from === 'me';
    const senderAddr = msg.senderAddr || (isOut ? accountEmail : counterpartyAddr);
    const senderName = isOut ? accountName : displayNameFromAddr(senderAddr);
    const senderEmail = extractEmail(senderAddr) || senderAddr;
    const isLast = index === messages.length - 1;
    if (collapsed) {
      const preview = mailPreviewLine(msg.text);
      return (
        <button
          key={msg.id}
          type="button"
          onClick={() => setOpenedIds((current) => (current.includes(msg.id) ? current : [...current, msg.id]))}
          className="flex w-full items-center gap-4 py-2 text-left hover:bg-app-hover"
        >
          <Avatar nameOrAddr={senderAddr} size={THREAD_AVATAR} />
          <span className="min-w-0 flex-1 truncate text-sm">
            <span className="font-semibold text-app-text">{senderName}</span>
            {preview ? <span className="font-normal text-app-text-subtle"> {preview}</span> : null}
          </span>
          <span className="shrink-0 text-xs text-app-text-subtle">{formatCollapsedStamp(msg.created_at)}</span>
        </button>
      );
    }
    const toLine = isOut
      ? selectedMailTo.join(', ')
      : accountEmail;
    return (
      <div
        key={msg.id}
        data-inbox-latest-message={isLast ? '' : undefined}
        className={isLast ? 'pt-3 pb-1' : 'mb-8 border-b border-app-border pb-8 pt-3'}
      >
        <div className="flex items-start gap-3 md:gap-4">
          <Avatar nameOrAddr={senderAddr} size={THREAD_AVATAR} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-1 md:gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-baseline gap-1.5 md:flex-wrap">
                  <span className="min-w-0 truncate text-[15px] font-semibold text-app-text md:text-sm">{senderName}</span>
                  {senderEmail ? <span className="hidden text-xs text-app-text-subtle md:inline">&lt;{senderEmail}&gt;</span> : null}
                  <span className="shrink-0 text-xs text-app-text-subtle md:hidden">{formatCollapsedStamp(msg.created_at)}</span>
                </div>
                {toLine ? <div className="mt-0.5 truncate text-xs text-app-text-subtle md:whitespace-normal">an {toLine}</div> : null}
              </div>
              <span className="hidden shrink-0 pt-0.5 text-xs text-app-text-subtle md:inline">{formatDetailStamp(msg.created_at)}</span>
              {isLast ? (
                <>
                  <button
                    type="button"
                    title="Markieren"
                    aria-label={selectedMail.starred ? 'Markierung entfernen' : 'Markieren'}
                    onClick={onToggleStar}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover md:h-7 md:w-7"
                  >
                    <Star className={`h-5 w-5 md:h-4 md:w-4 ${selectedMail.starred ? 'fill-amber-500 text-amber-500' : 'text-app-text-subtle'}`} strokeWidth={1.8} />
                  </button>
                  <button
                    type="button"
                    title="Antworten"
                    aria-label="Antworten"
                    onClick={() => setReplyOpen(true)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover md:h-7 md:w-7"
                  >
                    <Reply className="h-5 w-5 md:h-4 md:w-4" strokeWidth={1.8} />
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div className="md:pl-14">
            <div className="mt-4 text-[15px] leading-relaxed text-app-text md:mt-3 md:text-sm [&_img]:max-w-full">
            {msg.html ? (
              <div className="-mx-1 overflow-x-auto dark:rounded-lg dark:border dark:border-app-border dark:bg-white dark:p-3 md:mx-0">
              <div
                className={`${mailHtmlPreviewClassName(msg.html)} !rounded-none !border-0 !bg-transparent !p-0`}
                onClick={openAttachmentFromMailContent}
                dangerouslySetInnerHTML={{
                  __html: prepareMailHtmlForPreview(sanitizeMailHtml(msg.html), {
                    scopeId: `msg-${msg.id}`,
                  }),
                }}
              />
              </div>
            ) : (
              <div className="space-y-3">
                {splitMailPlainTextParagraphs(msg.text).map((paragraph, idx) => (
                  <p key={idx} className="whitespace-pre-wrap break-words">
                    {renderTextWithLinks(paragraph)}
                  </p>
                ))}
              </div>
            )}
            <MessageQuotedHistory
              msg={msg}
              sanitizeMailHtml={sanitizeMailHtml}
              renderTextWithLinks={renderTextWithLinks}
              onOpenAttachment={openAttachmentFromMailContent}
            />
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="mt-6">
                <p className="mb-2 text-xs text-app-text-subtle">
                  {msg.attachments.length === 1 ? '1 Anhang' : `${msg.attachments.length} Anhänge`}
                </p>
              <div className="flex flex-wrap gap-2">
                {msg.attachments.map((att, idx) => {
                  const downloadUrl =
                    msg.account_id && msg.external_message_id && att.id
                      ? buildInboxAttachmentDownloadUrl({
                          accountId: msg.account_id,
                          messageId: msg.external_message_id,
                          attachmentId: att.id,
                          filename: att.filename || 'attachment',
                        })
                      : null;
                  const previewTarget: InboxAttachmentPreviewTarget | null = downloadUrl
                    ? {
                        downloadUrl,
                        filename: att.filename || 'attachment',
                        contentType: att.content_type ?? null,
                      }
                    : null;
                  return (
                    <InboxAttachmentChip
                      key={`${att.id || att.filename}-${idx}`}
                      filename={att.filename || 'attachment'}
                      size={att.size}
                      contentType={att.content_type}
                      previewTarget={previewTarget}
                      onOpen={onOpenAttachment}
                    />
                  );
                })}
              </div>
              </div>
            )}
            </div>
          </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden bg-app-surface">
      <div ref={threadScrollRef} className="app-scrollbar min-h-0 flex-1 overflow-y-auto bg-app-surface px-4 pb-24 pt-2 md:px-8 md:py-6">
        <div className="mb-5 flex flex-wrap items-center gap-2.5 md:mb-6 md:pl-14">
          <h1 className="text-[22px] font-normal leading-snug text-app-text md:text-xl md:font-semibold">{selectedMail.subject || '(Kein Betreff)'}</h1>
          {isUrgent(selectedMail) ? (
            <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">Dringend</span>
          ) : null}
        </div>
        {messagesLoading && messages.length === 0 ? (
          <div className="space-y-3">
            <div className="h-24 rounded-lg bg-app-surface-muted" />
            <div className="h-16 rounded-lg bg-app-surface-muted" />
          </div>
        ) : (
          <ThreadMessages
            messages={messages}
            showEarlier={showEarlier}
            onShowEarlier={() => setShowEarlier(true)}
            isExpanded={(msg, index) => index === messages.length - 1 || openedIds.includes(msg.id)}
            renderMessage={renderMessage}
          />
        )}
        {(selectedMail.opened_count || selectedMail.link_click_count) ? (
          <p className="mt-4 text-xs md:pl-14 text-app-text-subtle">
            {selectedMail.opened_count ? `Geöffnet ${selectedMail.opened_count}×` : ''}
            {selectedMail.opened_count && selectedMail.link_click_count ? ' · ' : ''}
            {selectedMail.link_click_count ? `Link geklickt ${selectedMail.link_click_count}×` : ''}
          </p>
        ) : null}
        {!replyOpen ? (
          <div className="mt-6 flex gap-3 md:mt-4 md:flex-wrap md:gap-2.5 md:pl-14">
            <button
              type="button"
              onClick={() => setReplyOpen(true)}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-app-border-strong bg-app-surface px-4 text-sm font-medium text-app-text-muted hover:bg-app-hover md:h-9 md:flex-none"
            >
              <Reply className="h-4 w-4" strokeWidth={1.8} />
              Antworten
            </button>
            <button
              type="button"
              onClick={onForward}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-app-border-strong bg-app-surface px-4 text-sm font-medium text-app-text-muted hover:bg-app-hover md:h-9 md:flex-none"
            >
              <Forward className="h-4 w-4" strokeWidth={1.8} />
              Weiterleiten
            </button>
          </div>
        ) : null}
        <div className={replyOpen ? 'mt-6 md:mt-8 md:pl-14' : 'hidden'}>{compose}</div>
      </div>
    </div>
  );
}

function ThreadMessages({
  messages,
  showEarlier,
  onShowEarlier,
  isExpanded,
  renderMessage,
}: {
  messages: ChatMessage[];
  showEarlier: boolean;
  onShowEarlier: () => void;
  isExpanded: (msg: ChatMessage, index: number) => boolean;
  renderMessage: (msg: ChatMessage, index: number, collapsed: boolean) => React.ReactNode;
}) {
  const lastIndex = messages.length - 1;
  const older = messages.slice(0, lastIndex);
  const hideMiddle = older.length > 2 && !showEarlier;
  const shownOlder = hideMiddle ? [older[0], older[older.length - 1]] : older;
  const hiddenCount = hideMiddle ? older.length - 2 : 0;
  const latest = messages[lastIndex];

  const firstCollapsed = shownOlder[0] ? !isExpanded(shownOlder[0], 0) : false;

  return (
    <div>
      {shownOlder[0] ? (
        <div className={firstCollapsed && hiddenCount === 0 ? 'border-b border-app-border' : undefined}>
          {renderMessage(shownOlder[0], 0, firstCollapsed)}
        </div>
      ) : null}
      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={onShowEarlier}
          title={`${hiddenCount} weitere Nachrichten anzeigen`}
          className="flex w-full items-center gap-4 py-2 text-left hover:bg-app-hover"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-app-border-strong bg-app-surface text-sm font-medium text-app-text-muted">
            {hiddenCount}
          </span>
          <span className="h-px min-w-0 flex-1 bg-app-border" />
        </button>
      ) : null}
      {shownOlder.slice(1).map((msg) => {
        const index = messages.indexOf(msg);
        const collapsed = !isExpanded(msg, index);
        return (
          <div key={msg.id} className={collapsed ? 'border-b border-app-border' : undefined}>
            {renderMessage(msg, index, collapsed)}
          </div>
        );
      })}
      {latest ? renderMessage(latest, lastIndex, false) : null}
    </div>
  );
}

function mailPreviewLine(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function formatCollapsedStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const dayDiff = Math.round((startToday - start) / 86_400_000);
  if (dayDiff <= 0) return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (dayDiff === 1) return 'Gestern';
  if (dayDiff < 14) return `vor ${dayDiff} Tagen`;
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

function formatDetailStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
}
